
class BankListViewController extends BaseTableViewController
{
    static newInstance() : BankListViewController {
        let vc = new BankListViewController("bank-list-view")
        vc.initWithResource("layout/bankaccount/BankListView.html")
        return vc;
    }

    private addButton:MUIButton = null;
    private totalLabel:MUILabel = null;

    viewDidLoad(){
        super.viewDidLoad();

        this.addButton = MUIOutlet(this, "add-btn", "MUIButton");
        this.addButton.setAction(this, this.addBankAction);

        this.totalLabel = MUIOutlet(this, "total-lbl", "MUILabel");

        this.tableView = MUIOutlet(this, "table-view", "UITableView");
        this.tableView.dataSource = this;
        this.tableView.delegate = this;

        // The movements screen categorizes and links movements: the banks come again with their
        // status (the pending counts live in a view on the server, read with the bank)
        MIONotificationCenter.defaultCenter().addObserver(this, BankListViewController.statusDidChangeNotification, function(this:BankListViewController){
            this.fetchedResultsController = null;
            this.tableView.reloadData();
        });

        // The balance rows are a different entity, so the fetched results controller of the banks
        // doesn't redraw when only they change (after the movements screen changed a movement
        // the banks come back unchanged): the pills are read again when the store refreshes them
        MIONotificationCenter.defaultCenter().addObserver(this, MIOManagedObjectContextObjectsDidChange, function(this:BankListViewController, notification:MIONotification){
            let refreshed = notification.userInfo?.[MIORefreshedObjectsKey];
            if (refreshed?.["BankMovementBalance"] == null) return;
            if (this.viewIsLoaded == false) return;
            this.tableView.reloadData();
        });

        this.tableView.reloadData();
    }

    static statusDidChangeNotification = "BankMovementStatusDidChangeNotification";

    cellAtIndexPath(tableview, indexPath:MIOIndexPath){
        let cell = tableview.dequeueReusableCellWithIdentifier('BankCell') as BankCell;
        let item = this.fetchedResultsController.objectAtIndexPath(indexPath) as Bank;

        cell.setItem(item, this.balanceOf(item));
        cell.selected = (item.identifier == this.selectedItem?.identifier);

        return cell;
    }

    protected reloadData(){
        super.reloadData();
        this.updateTotal();
    }

    controllerDidChangeContent(controller:MIOFetchedResultsController){
        super.controllerDidChangeContent(controller);
        this.updateTotal();
    }

    private updateTotal(){
        if (this.totalLabel == null) return;
        let cf = (MUIWebApplication.sharedInstance().delegate as AppDelegate).currencyFormatter;
        let banks = (this.fetchedResultsController.fetchedObjects ?? []) as Bank[];
        let total = 0;
        for (let index = 0; index < banks.length; index++) total += this.balanceOf(banks[index]);
        this.totalLabel.text = cf.stringFromNumber(total);
    }

    // The balance of a bank is the sum of its accounts (a bank can have several of them): they
    // come with the bank, as a relationship of the same fetch
    private balanceOf(bank:Bank) : number {
        let total = 0;
        let accounts = (bank.accounts?.allObjects ?? []) as BankAccount[];
        for (let account of accounts) {
            if (account.deletedAt != null) continue;
            total += account.balance ?? 0;
        }
        return total;
    }

    didSelectCellAtIndexPath(tableView:UITableView, indexPath:MIOIndexPath){
        this.selectedItem = this.fetchedResultsController.objectAtIndexPath(indexPath) as Bank;
        this.tableView.reloadData();

        let vc = BankDetailViewController.newInstance();
        vc.item = this.selectedItem;

        let nc = new MUINavigationController();
        nc.initWithRootViewController(vc);

        this.splitViewController.showDetailViewController(nc);
    }

    protected _fetchedResultsController:MIOFetchedResultsController = null;
    set fetchedResultsController(value:MIOFetchedResultsController){
        if (value == null && this._fetchedResultsController != null)
            this._fetchedResultsController.delegate = null;

        this._fetchedResultsController = value;
    }

    get fetchedResultsController(){
        if (this._fetchedResultsController != null)
            return this._fetchedResultsController;

        let ad = MUIWebApplication.sharedInstance().delegate;

        let sds = [ MIOSortDescriptor.sortDescriptorWithKey("name", true) ];

        let fetchRequest = DBHelper.listFetchRequestWithEntityName("Bank", sds, null);
        // One fetch brings everything the bank screens read: the accounts (their balances add up
        // to the bank's) with their info and pending counts, since the accounts list and the
        // account strip draw them from memory as soon as a bank is clicked, and the pending
        // counts of the bank
        fetchRequest.relationshipKeyPathsForPrefetching = ["accounts", "accounts.info", "accounts.movementBalance", "movementBalance"];

        let fetchedResultsController = new MIOFetchedResultsController();
        fetchedResultsController.initWithFetchRequest(fetchRequest, ad.managedObjectContext, null);
        fetchedResultsController.delegate = this;

        fetchedResultsController.performFetch();

        this._fetchedResultsController = fetchedResultsController;

        return this._fetchedResultsController;
    }

    private addBankAction() {

        let avc = new MUIAlertViewController();
        avc.initWithTitle(MIOLocalizeString("ADD_BANK_OPTIONS", "ADD BANK OPTIONS"), MIOLocalizeString("CHOOSE_ONE_OPTION", "CHOOSE ONE OPTION"), MUIAlertViewStyle.Default);

        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("MANUAL", "MANUAL"), MUIAlertActionStyle.Default, this, function(this:BankListViewController) {
            this.addCustomBankAction();
        }));

        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("OPEN_BANKING_API", "OPEN BANKING API"), MUIAlertActionStyle.Default, this, function(this:BankListViewController) {
            this.addBankWithOpentBankingAPI();
        }));

        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("CANCEL", "CANCEL"), MUIAlertActionStyle.Cancel, null, null));

        this.presentViewController(avc, true);
    }    

    private addCustomBankAction() {

        let avc = new MUIAlertViewController();
        avc.initWithTitle(MIOLocalizeString("ADD_BANK", "ADD BANK"), MIOLocalizeString("ADD_BANK_NAME", "ADD BANK NAME"), MUIAlertViewStyle.Default);

        avc.addTextFieldWithConfigurationHandler(this, function(textField:MUITextField) {
            textField.placeholderText = MIOLocalizeString("NAME", "NAME");
        });

        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("OK", "OK"), MUIAlertActionStyle.Default, this, function(this:BankListViewController) {
            let name = avc.textFields[0].text;
            this.addBank(name);
        }));

        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("CANCEL", "CANCEL"), MUIAlertActionStyle.Cancel, null, null));

        this.presentViewController(avc, true);
    }    

    private addBank(name:string) {
        if (name == null) { AppHelper.showErrorMessage(this, MIOLocalizeString("BANK_ERROR_CREATION", "BANK ERROR CREATION"), MIOLocalizeString("THE_NAME_IS_EMPTY", "THE NAME IS EMPTY")); return;}

        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        let bank = MIOEntityDescription.insertNewObjectForEntityForName("Bank", ad.managedObjectContext) as Bank;
        bank.identifier = MIOUUID.UUID().UUIDString;
        bank.name = name;

        ad.managedObjectContext.save();
    }

    private addBankWithOpentBankingAPI() {
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        ad.webService.openBankingLink();
    }

    // private showNoItemSelectionViewController(){
    //     let vc = AppHelper.sharedInstance().emptyViewControllerForIndentifier("BankAccount", NoItemSelectedViewControllerType.BankAccount);
    //     this.navigationController.splitViewController.showDetailViewController(vc);   
    // }

}